import type { SanityGameweekRange, StatMode, StatsVenue } from './types';
import type { PlayerStatsRow } from '@api-service/player-stats';

export const STATS_FIELD_GROUPS = 'attack,defending,fpl';
export const DEFAULT_STAT_MODE: StatMode = 'attacking';
export const DEFAULT_VENUE: StatsVenue = 'all';

type StatDefinition = {
  label: string;
  path: string;
  decimals?: number;
};

// Mirrors the social-image cards so an article card and a tweet about the same
// player show the same numbers in the same order.
const PROFILE_STATS: Record<StatMode, StatDefinition[]> = {
  attacking: [
    { label: 'Appearances', path: 'participation.appearances' },
    { label: 'Goals', path: 'attack.goals' },
    { label: 'xG', path: 'attack.expectedGoals', decimals: 2 },
    { label: 'Assists', path: 'attack.assists' },
    { label: 'xA', path: 'attack.expectedAssists', decimals: 2 },
    { label: 'Shots on target', path: 'attack.shots.onTarget' },
    { label: 'Shots in the box', path: 'attack.shots.inBox' },
    { label: 'Big chances', path: 'attack.bigChances' },
    { label: 'Key passes', path: 'attack.keyPasses' },
    { label: 'Big chances created', path: 'attack.bigChancesCreated' },
    { label: 'FPL points', path: 'fpl.points' },
    { label: 'DefCon points', path: 'fpl.defensiveContribution.points' }
  ],
  defending: [
    { label: 'Appearances', path: 'participation.appearances' },
    { label: 'Clean sheets', path: 'defending.cleanSheets' },
    { label: 'xCS', path: 'defending.expectedCleanSheets', decimals: 2 },
    { label: 'DefCon points', path: 'fpl.defensiveContribution.points' },
    { label: 'Goals', path: 'attack.goals' },
    { label: 'xG', path: 'attack.expectedGoals', decimals: 2 },
    { label: 'Assists', path: 'attack.assists' },
    { label: 'xA', path: 'attack.expectedAssists', decimals: 2 },
    { label: 'FPL points', path: 'fpl.points' },
    { label: 'Big chances', path: 'attack.bigChances' },
    { label: 'Big chances created', path: 'attack.bigChancesCreated' },
    { label: 'Key passes', path: 'attack.keyPasses' }
  ]
};

const HEAD_TO_HEAD_STATS: Record<StatMode, StatDefinition[]> = {
  attacking: [
    { label: 'Appearances', path: 'participation.appearances' },
    { label: 'Goals', path: 'attack.goals' },
    { label: 'Expected goals', path: 'attack.expectedGoals', decimals: 2 },
    { label: 'Assists', path: 'attack.assists' },
    { label: 'Expected assists', path: 'attack.expectedAssists', decimals: 2 },
    { label: 'DefCon points', path: 'fpl.defensiveContribution.points' },
    { label: 'Big chances', path: 'attack.bigChances' },
    { label: 'Big chances created', path: 'attack.bigChancesCreated' }
  ],
  defending: [
    { label: 'Appearances', path: 'participation.appearances' },
    { label: 'Clean sheets', path: 'defending.cleanSheets' },
    {
      label: 'Expected clean sheets',
      path: 'defending.expectedCleanSheets',
      decimals: 2
    },
    { label: 'DefCon points', path: 'fpl.defensiveContribution.points' },
    { label: 'Goals', path: 'attack.goals' },
    { label: 'Expected goals', path: 'attack.expectedGoals', decimals: 2 },
    { label: 'Assists', path: 'attack.assists' },
    { label: 'Expected assists', path: 'attack.expectedAssists', decimals: 2 }
  ]
};

export type StatRow = { label: string; value: string };
export type ComparisonRow = {
  label: string;
  left: string;
  right: string;
  leader: 'left' | 'right' | null;
};

export const buildStatsRequestKey = (
  venue: StatsVenue,
  range: SanityGameweekRange | undefined
): string => `${venue}:${range?.minGameweek ?? ''}-${range?.maxGameweek ?? ''}`;

export const readStat = (row: unknown, path: string): number | undefined => {
  const value = path
    .split('.')
    .reduce<unknown>(
      (current, key) =>
        current && typeof current === 'object'
          ? (current as Record<string, unknown>)[key]
          : undefined,
      row
    );
  return typeof value === 'number' ? value : undefined;
};

export const formatStat = (
  value: number | undefined,
  decimals?: number
): string => {
  if (value === undefined) return '-';
  if (decimals !== undefined) return value.toFixed(decimals);
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
};

export const buildStatRows = (
  row: PlayerStatsRow | undefined,
  mode: StatMode
): StatRow[] =>
  PROFILE_STATS[mode].map(({ label, path, decimals }) => ({
    label,
    value: formatStat(readStat(row, path), decimals)
  }));

export const buildComparisonRows = (
  left: PlayerStatsRow | undefined,
  right: PlayerStatsRow | undefined,
  mode: StatMode
): ComparisonRow[] =>
  HEAD_TO_HEAD_STATS[mode].map(({ label, path, decimals }) => {
    const leftValue = readStat(left, path);
    const rightValue = readStat(right, path);
    let leader: ComparisonRow['leader'] = null;
    if (leftValue !== undefined && rightValue !== undefined) {
      if (leftValue > rightValue) leader = 'left';
      if (rightValue > leftValue) leader = 'right';
    }
    return {
      label,
      left: formatStat(leftValue, decimals),
      right: formatStat(rightValue, decimals),
      leader
    };
  });
